import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import NewsCard from "@/components/feature/NewsCard";
import SpecialCard from "@/components/feature/SpecialCard";
import useSeo from "@/hooks/useSeo";
import { allNews, allMovies, allSpecials } from "@/utils/data";

const STEP = 4;

export default function NewsList() {
  useSeo({
    title: "影视资讯 - 百合影视新闻动态",
    description:
      "百合影视资讯频道，汇总百合题材电影与电视剧的最新动态、幕后花絮、深度盘点与访谈内容，第一时间掌握行业资讯。",
    keywords: "百合影视资讯,百合新闻,百合电影动态,电视剧资讯,影视盘点",
  });

  const [visible, setVisible] = useState(STEP);

  const sorted = useMemo(
    () => [...allNews].sort((a, b) => b.date.localeCompare(a.date)),
    [],
  );
  const shown = sorted.slice(0, visible);
  const hasMore = visible < sorted.length;

  const hotMovies = useMemo(
    () => [...allMovies].sort((a, b) => b.rating - a.rating).slice(0, 4),
    [],
  );

  return (
    <div className="pt-24 md:pt-32">
      <div className="border-b border-background-200/60 bg-secondary-50">
        <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <Breadcrumb
            items={[{ label: "首页", to: "/" }, { label: "新闻资讯" }]}
          />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            新闻资讯
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            关注百合题材影视的最新动态。从首映红毯到幕后花絮，从深度盘点到主创访谈，帮你不错过每一个值得期待的瞬间。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
        <div className="grid gap-10 lg:grid-cols-[1fr_320px] lg:gap-12">
          <div>
            {shown.length > 0 ? (
              <>
                <div className="space-y-4">
                  {shown.map((item) => (
                    <NewsCard key={item.id} item={item} />
                  ))}
                </div>
                {hasMore ? (
                  <div className="mt-8 flex justify-center">
                    <button
                      type="button"
                      onClick={() => setVisible((v) => v + STEP)}
                      className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 px-6 py-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                    >
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className="ri-add-line" />
                      </span>
                      加载更多资讯
                    </button>
                  </div>
                ) : (
                  <p className="mt-8 text-center text-xs text-foreground-400">
                    已经到底啦，没有更多资讯了
                  </p>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300/70 bg-background-50 px-6 py-20 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-3xl text-foreground-400">
                  <i className="ri-newspaper-line" />
                </span>
                <h2 className="mt-5 font-heading text-lg font-semibold text-foreground-950">
                  暂无新闻资讯
                </h2>
                <p className="mt-2 text-sm text-foreground-600">
                  内容正在准备中，先去看看影视推荐吧。
                </p>
                <Link
                  to="/category"
                  className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  浏览影视分类
                </Link>
              </div>
            )}
          </div>

          <aside className="space-y-8">
            <div className="rounded-lg border border-background-200/70 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                <span className="flex h-4 w-4 items-center justify-center text-accent-500">
                  <i className="ri-fire-line" />
                </span>
                热门作品
              </h2>
              <ul className="mt-4 space-y-3">
                {hotMovies.map((movie, index) => (
                  <li key={movie.id}>
                    <Link
                      to={`/detail/${movie.id}`}
                      className="group flex items-center gap-3"
                    >
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-xs font-semibold text-foreground-500">
                        {index + 1}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm text-foreground-700 transition-colors group-hover:text-primary-600">
                        {movie.title}
                      </span>
                      <span className="shrink-0 text-xs text-accent-600">
                        {movie.rating.toFixed(1)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="font-heading text-base font-semibold text-foreground-950">
                推荐专题
              </h2>
              <div className="mt-4 space-y-4">
                {allSpecials.slice(0, 2).map((special) => (
                  <SpecialCard key={special.id} special={special} compact />
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}